import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Mail,
  Phone,
  MessageSquare,
  ArrowRight,
  Check,
  Loader2
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { SMS_NUMBER_DISPLAY } from '../constants/contact';

const SOCIAL_ICONS = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
};

const DEFAULT_FOOTER = {
  description: 'The revolutionary home services platform where competitive bidding meets quality craftsmanship. Get better prices, faster service, and peace of mind for all your home improvement needs.',
  phone: '+1 279-278-4408',
  sms: SMS_NUMBER_DISPLAY,
  email: 'admin@fixdirectly.com',
  copyright_text: '© 2025 FixDirectly. All rights reserved.',
  platform_links: [
    { label: 'How It Works', url: '#how-it-works' },
    { label: 'Services', url: '/services' },
    { label: 'Group Buy Now', url: '/group-buy' },
    { label: 'Blog', url: '/blog' },
    { label: 'Become a Partner', url: '/partner' },
  ],
  legal_links: [
    { label: 'Terms of Use', url: '/terms-of-use' },
    { label: 'Privacy Policy', url: '/privacy-policy' },
  ],
  social_links: [],
};

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [footerData, setFooterData] = useState(DEFAULT_FOOTER);

  // Blog is a core navigation destination. Keep it available even when the
  // rest of the platform links are supplied by editable admin footer settings.
  const platformLinks = footerData.platform_links.some((link) => link.url === '/blog')
    ? footerData.platform_links
    : [...footerData.platform_links, { label: 'Blog', url: '/blog' }];

  useEffect(() => {
    apiService.get('/api/v1/footer')
      .then((res) => {
        const data = res.footer ?? res;
        if (data && typeof data === 'object') {
          setFooterData({
            ...DEFAULT_FOOTER,
            ...data,
            platform_links: data.platform_links?.length ? data.platform_links : DEFAULT_FOOTER.platform_links,
            legal_links: data.legal_links?.length ? data.legal_links : DEFAULT_FOOTER.legal_links,
            social_links: data.social_links || [],
          });
        }
      })
      .catch(() => {
        // silently fall back to defaults
      });
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }

    try {
      setIsLoading(true);
      setStatus({ type: '', message: '' });

      await apiService.post('/api/v1/subscribe', {
        subscriber: {
          email: email,
          source: 'homepage_footer'
        }
      });

      setStatus({ type: 'success', message: 'Thanks for subscribing!' });
      setEmail('');

      // Analytics is not needed for initial rendering. Load it only after a
      // successful subscription so Firebase stays out of the critical bundle.
      Promise.all([
        import('../config/firebase'),
        import('firebase/analytics'),
      ])
        .then(([{ analytics }, { logEvent }]) => {
          if (analytics) logEvent(analytics, 'newsletter_subscribe');
        })
        .catch(() => {
          // Subscription already succeeded; analytics must never block UX.
        });
    } catch (error) {
      const errorMessage = error.message || 'Something went wrong. Please try again.';
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <footer className="bg-gray-900 text-white">
      {/* Newsletter Section */}
      <div className="bg-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">
              Stay Updated with FixDirectly
            </h3>
            <p className="text-gray-300 mb-8">
              Get tips on home improvement, platform updates, and exclusive offers delivered to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-emerald-500"
              />
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-emerald-600 hover:bg-emerald-700 px-6 shrink-0 group"
              >
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : status.type === 'success' ? (
                  <>
                    <Check size={16} className="mr-2" />
                    Subscribed
                  </>
                ) : (
                  <>
                    Subscribe
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </>
                )}
              </Button>
            </form>
            {status.message && (
              <p className={`mt-3 text-sm ${status.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                {status.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              {/* Text logo matches the Header treatment so brand stays
                  consistent across nav and footer. */}
              <Link
                to="/"
                className="inline-block text-2xl font-bold text-emerald-500 hover:text-emerald-400 transition-colors duration-200 mb-4"
              >
                FixDirectly
              </Link>
              <p className="text-gray-300 mb-6 max-w-md">
                {footerData.description}
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                {footerData.phone && (
                  <div className="flex items-center space-x-3 text-gray-300">
                    <Phone size={16} className="text-emerald-400" />
                    <span>{footerData.phone}</span>
                  </div>
                )}
                {footerData.email && (
                  <div className="flex items-center space-x-3 text-gray-300">
                    <Mail size={16} className="text-emerald-400" />
                    <span>{footerData.email}</span>
                  </div>
                )}
                {/* SMS lead-alert number — always shown (falls back to the
                    constant if the API footer config omits it). */}
                <div className="flex items-center space-x-3 text-gray-300">
                  <MessageSquare size={16} className="text-emerald-400" />
                  <span>Text/SMS: {footerData.sms || SMS_NUMBER_DISPLAY}</span>
                </div>
              </div>
            </div>

            {/* Platform Links */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Platform</h4>
              <ul className="space-y-2">
                {platformLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.url}
                      className="text-gray-300 hover:text-emerald-400 transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Legal</h4>
              <ul className="space-y-2">
                {footerData.legal_links.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.url}
                      className="text-gray-300 hover:text-emerald-400 transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Social Links & Bottom Bar */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              {/* Social Links */}
              <div className="flex items-center space-x-4 mb-4 md:mb-0">
                <span className="text-gray-400 text-sm">Follow us:</span>
                <div className="flex space-x-3">
                  {footerData.social_links.length > 0 ? (
                    footerData.social_links.map((social, index) => {
                      const Icon = SOCIAL_ICONS[social.platform] || Facebook;
                      return (
                        <a
                          key={index}
                          href={social.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Follow FixDirectly on ${social.platform || 'social media'}`}
                          className="text-gray-400 hover:text-emerald-400 transition-colors duration-200"
                        >
                          <Icon size={20} />
                        </a>
                      );
                    })
                  ) : (
                    <>
                      <a href="#" aria-label="Follow FixDirectly on Facebook" className="text-gray-400 hover:text-emerald-400 transition-colors duration-200">
                        <Facebook size={20} />
                      </a>
                      <a href="#" aria-label="Follow FixDirectly on X" className="text-gray-400 hover:text-emerald-400 transition-colors duration-200">
                        <Twitter size={20} />
                      </a>
                      <a href="#" aria-label="Follow FixDirectly on Instagram" className="text-gray-400 hover:text-emerald-400 transition-colors duration-200">
                        <Instagram size={20} />
                      </a>
                      <a href="#" aria-label="Follow FixDirectly on LinkedIn" className="text-gray-400 hover:text-emerald-400 transition-colors duration-200">
                        <Linkedin size={20} />
                      </a>
                    </>
                  )}
                </div>
              </div>

              {/* Copyright */}
              <div className="text-gray-400 text-sm">
                {footerData.copyright_text}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};
